import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Input, Modal } from '@bruno/ui';
import { createWebsite, listWebsites } from '../api/websites';

export function WebsitesListPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['websites'],
    queryFn: listWebsites,
  });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: createWebsite,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['websites'] });
      setOpen(false);
      setName('');
      setDomain('');
    },
    onError: (err: Error) => setError(err.message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate({ name, domain: domain.toLowerCase() });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Websites</h1>
          <p className="text-sm text-slate-500">Add a website to embed Bruno on it.</p>
        </div>
        <Button onClick={() => setOpen(true)}>+ Add website</Button>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Loading...</div>
      ) : !data || data.websites.length === 0 ? (
        <Card className="text-center py-16">
          <h2 className="text-lg font-semibold mb-2">No websites yet</h2>
          <p className="text-sm text-slate-500 mb-4">Add your first website to get an embed snippet.</p>
          <Button onClick={() => setOpen(true)}>+ Add website</Button>
        </Card>
      ) : (
        <div className="grid gap-3">
          {data.websites.map((w) => (
            <Link key={w.id} to={`/websites/${w.id}`}>
              <Card className="hover:border-indigo-300 transition-colors cursor-pointer">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{w.name}</div>
                    <div className="text-sm text-slate-500">{w.domain}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge tone={w.status === 'active' ? 'green' : w.status === 'paused' ? 'amber' : 'red'}>
                      {w.status}
                    </Badge>
                    <span className="text-slate-400">→</span>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add a website">
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My SaaS"
            required
            autoFocus
          />
          <Input
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com or *.example.com"
            hint="Use *.example.com to let Bruno work across every subdomain (app.example.com, dashboard.example.com, ...) and navigate between them. A plain example.com covers only the apex and www."
            required
          />
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Add website
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
